import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Receipt } from 'lucide-react';
import type { TenantSummary } from '@forq/contracts';
import type { MachinesGateway } from '@forq/machines-data';
import type { MasterdataGateway } from '@forq/masterdata-data';
import type { MastersGateway } from '@forq/masters-data';
import type { OrderGateway, OrderRecord } from '@forq/orders-data';
import {
  invoiceFlowTransitions,
  invoiceStatusLabels,
  paymentStatusLabels,
  type FinanceGateway,
  type Invoice,
  type InvoiceDocumentStatus,
  type InvoicePaymentStatus,
} from '@forq/finance-data';
import { FlowTrail, StatusPill, type StatusTone } from '@forq/ui';
import { buildInvoiceFlow } from '../finance/invoiceFlow';
import { findOrderInvoice, resolveOrderInvoiceLines, suggestCostCenterId } from '../finance/orderBilling';

interface OrderBillingPanelProps {
  readonly tenant: TenantSummary;
  readonly order: OrderRecord;
  readonly permissions: ReadonlySet<string>;
  readonly financeGateway: FinanceGateway;
  readonly ordersGateway: OrderGateway;
  readonly machinesGateway: MachinesGateway;
  readonly masterdataGateway: MasterdataGateway;
  readonly mastersGateway: MastersGateway;
  /** Nummer der Rechnungs-Station in der Belegstrecke (ARISTA-Center) -- rendert das Nummern-Badge. */
  readonly stepNumber?: number;
  /** Wird nach dem Erstellen der Rechnung aufgerufen -- die Liste lädt neu, der Vorgangs-Status im
   *  Fenster springt sofort auf „Abgerechnet". */
  readonly onBilled?: () => void;
}

const documentTone: Record<InvoiceDocumentStatus, StatusTone> = {
  draft: 'neutral', in_review: 'warning', approved: 'info', invoiced: 'info', sent: 'progress', cancelled: 'danger',
};
const paymentTone: Record<InvoicePaymentStatus, StatusTone> = {
  not_relevant: 'neutral', open: 'warning', partial: 'warning', paid: 'done',
};

function money(value: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
}
function messageOf(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Der Vorgang konnte nicht abgerechnet werden.';
}

/**
 * Abrechnung direkt am Vorgang (Vorgang-Zentrierung, Etappe 1): Rechnung erstellen → prüfen/freigeben →
 * erstellen (RE-Nr.) → versenden. Nach dem Versand ist die Rechnung schreibgeschützt und liegt nur noch
 * lesend in Finance → Ausgangsrechnungen. Nutzt exakt dieselbe Rechnungs-Maschinerie wie die
 * Finance-Abrechnung (createInvoice/transitionInvoice), nur am neuen Ort.
 */
export function OrderBillingPanel({
  tenant, order, permissions, financeGateway, ordersGateway, machinesGateway, masterdataGateway, mastersGateway, stepNumber, onBilled,
}: OrderBillingPanelProps) {
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canBill = permissions.has('finance.invoices.edit');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const invoices = await financeGateway.listInvoices({ tenantId: tenant.id });
      setInvoice(findOrderInvoice(invoices, order.number));
    } catch (loadError) {
      setError(messageOf(loadError));
    } finally {
      setLoading(false);
    }
  }, [financeGateway, tenant.id, order.number]);

  useEffect(() => { void load(); }, [load]);

  const createInvoice = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const costCenters = await mastersGateway.listCostCenters(tenant.id);
      const lines = await resolveOrderInvoiceLines(order, tenant.id, machinesGateway, masterdataGateway);
      // Beleggetrieben: steht der Vorgang noch „In Arbeit" (in-progress/reported), ihn erst auf
      // billing-ready heben, damit der anschließende Wechsel auf „Abgerechnet" (billed) über den
      // erlaubten Graphpfad geht. Best-effort — die Rechnung entsteht auch ohne den Statuswechsel.
      if (order.status === 'in-progress' || order.status === 'reported') {
        try {
          await ordersGateway.transitionOrder({
            tenantId: tenant.id, orderId: order.id, toStatus: 'billing-ready',
            reason: 'Abrechnung im Vorgang gestartet',
          });
        } catch { /* nachrangig */ }
      }
      const created = await financeGateway.createInvoice({
        tenantId: tenant.id,
        customerNumber: order.customerNumber,
        customerName: order.customerName,
        sourceType: 'order',
        sourceNumber: order.number,
        costCenterId: order.conditions.costCenterId || suggestCostCenterId(costCenters, 'order'),
        language: order.conditions.language,
        // Leistungsdatum (§ 14 UStG): das Belegdatum des Vorgangs; fehlt es, setzt die Ablage heute.
        serviceDate: order.conditions.documentDate,
        lines,
      });
      // Vorgang als berechnet markieren, damit er die Liste „Bereit zur Abrechnung" verlässt. Schlägt der
      // Statuswechsel fehl (z. B. fehlende Vorgangs-Rechte), bleibt die Rechnung — der Doppel-Riegel greift
      // serverseitig beim erneuten Anlegen.
      try {
        await ordersGateway.transitionOrder({
          tenantId: tenant.id, orderId: order.id, toStatus: 'billed',
          reason: `Rechnung ${created.proposalNumber} erstellt`,
        });
      } catch { /* Statuswechsel ist nachrangig. */ }
      setInvoice(created);
      onBilled?.(); // Liste neu laden -> Vorgangs-Status im Fenster springt auf „Abgerechnet".
    } catch (createError) {
      setError(messageOf(createError));
    } finally {
      setBusy(false);
    }
  }, [financeGateway, ordersGateway, mastersGateway, machinesGateway, masterdataGateway, order, tenant.id, onBilled]);

  const advance = useCallback(async (toStatus: InvoiceDocumentStatus) => {
    if (!invoice) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await financeGateway.transitionInvoice({ tenantId: tenant.id, invoiceId: invoice.id, toStatus });
      setInvoice(updated);
    } catch (transitionError) {
      setError(messageOf(transitionError));
    } finally {
      setBusy(false);
    }
  }, [invoice, financeGateway, tenant.id]);

  const transitions = invoice ? invoiceFlowTransitions(invoice, permissions) : [];
  // Abrechenbar ab der Phase „In Arbeit" (beleggetrieben) — der interne billing-ready-Hop in
  // createInvoice hebt den Vorgang bei Bedarf erst dorthin. billed nur als Rest, falls noch offen.
  const canCreateHere = !invoice && ['in-progress', 'reported', 'billing-ready', 'billed'].includes(order.status);

  return (
    <section className="orders-billing-panel" aria-label="Abrechnung">
      <header>
        {stepNumber ? <span className="orders-belegstrecke__num">{stepNumber}</span> : null}
        <div>
          <span>Abrechnung</span>
          <h2>Rechnung zum Vorgang {order.number}</h2>
          <p>Rechnung hier erstellen und versenden. Nach dem Versand ist sie schreibgeschützt und liegt in Finance → Ausgangsrechnungen.</p>
        </div>
      </header>

      {error ? <div className="orders-billing-panel__message is-error">{error}</div> : null}

      {loading ? (
        <p className="orders-billing-panel__hint">Abrechnung wird geladen …</p>
      ) : invoice ? (
        <div className="orders-billing-panel__invoice">
          <FlowTrail steps={buildInvoiceFlow(invoice)} ariaLabel={`Belegstatus ${invoice.proposalNumber}`} />
          <div className="orders-billing-panel__summary">
            <span className="finance-pills">
              <StatusPill label={invoiceStatusLabels[invoice.documentStatus]} tone={documentTone[invoice.documentStatus]} />
              <StatusPill label={paymentStatusLabels[invoice.paymentStatus]} tone={paymentTone[invoice.paymentStatus]} />
            </span>
            <strong>{invoice.invoiceNumber ?? invoice.proposalNumber}</strong>
            <span className="orders-billing-panel__total">{money(invoice.grossTotal)}</span>
          </div>

          <div className="orders-billing-panel__actions">
            {transitions.map((transition) => (
              <button
                key={transition.toStatus}
                type="button"
                className={`finance-actions__btn${transition.tone === 'danger' ? ' is-danger' : ''}`}
                disabled={busy}
                onClick={() => void advance(transition.toStatus)}
              >
                {transition.label}
              </button>
            ))}
            <button
              type="button"
              className="finance-actions__btn"
              onClick={() => navigate(`/finance/rechnungen/${invoice.invoiceNumber ?? invoice.proposalNumber}`)}
            >
              <FileText size={15} /> Rechnung anzeigen
            </button>
          </div>

          {invoice.documentStatus === 'sent' ? (
            <p className="orders-billing-panel__hint is-done">Versendet — die Rechnung ist schreibgeschützt und liegt in Finance → Ausgangsrechnungen.</p>
          ) : null}
        </div>
      ) : canCreateHere ? (
        canBill ? (
          <button type="button" className="finance-actions__btn is-primary" disabled={busy} onClick={() => void createInvoice()}>
            <Receipt size={15} /> {busy ? 'Rechnung wird erstellt …' : 'Rechnung erstellen'}
          </button>
        ) : (
          <p className="orders-billing-panel__hint">Zum Abrechnen fehlt die Berechtigung (Finance · Rechnungen bearbeiten).</p>
        )
      ) : (
        <p className="orders-billing-panel__hint">Sobald der Vorgang „In Arbeit" ist, kann hier die Rechnung erstellt werden.</p>
      )}
    </section>
  );
}
